// Dictate → extract → review → commit Time Study drafts.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  inputClass,
  mfgPrimaryBtnLgClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useManufacturingDictateSession } from "@/components/manufacturing/useManufacturingDictateSession";
import {
  createTimeStudyEntry,
  extractTimeStudyFromTranscript,
  type TimeStudyDraftEntry,
} from "@/lib/manufacturing/timeStudyClient";
import {
  durationSecondsToMinutesInput,
  minutesInputToSeconds,
} from "@/lib/manufacturing/timeStudyFormat";

type Phase =
  | { kind: "record" }
  | { kind: "extracting" }
  | { kind: "review"; drafts: TimeStudyDraftEntry[] }
  | { kind: "error"; message: string };

type Props = {
  sessionId: string;
  onClose: () => void;
  onSaved: () => void;
};

export function TimeStudyDictateModal({ sessionId, onClose, onSaved }: Props) {
  const dictate = useManufacturingDictateSession();
  const [phase, setPhase] = useState<Phase>({ kind: "record" });
  const [drafts, setDrafts] = useState<TimeStudyDraftEntry[]>([]);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const autoExtractRef = useRef(false);

  const runExtract = useCallback(async (transcript: string) => {
    setPhase({ kind: "extracting" });
    const result = await extractTimeStudyFromTranscript(transcript);
    if (!result.ok) {
      setPhase({
        kind: "error",
        message: result.error || "Could not extract observations. Try again with subject + duration.",
      });
      return;
    }
    const draftsOut = result.data.drafts ?? [];
    if (!draftsOut.length) {
      setPhase({
        kind: "error",
        message: "Could not extract observations. Try again with subject + duration.",
      });
      return;
    }
    setDrafts(draftsOut);
    setPhase({ kind: "review", drafts: draftsOut });
  }, []);

  useEffect(() => {
    if (dictate.state !== "stopped" || !dictate.transcript.trim()) return;
    if (autoExtractRef.current) return;
    autoExtractRef.current = true;
    void runExtract(dictate.transcript);
  }, [dictate.state, dictate.transcript, runExtract]);

  const updateDraft = (idx: number, patch: Partial<TimeStudyDraftEntry>) => {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  const removeDraft = (idx: number) => {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
  };

  const commit = async () => {
    setSaveBusy(true);
    setSaveError(null);
    try {
      for (const d of drafts) {
        if (!d.subject_label.trim() || d.duration_seconds <= 0) {
          setSaveError("Each draft needs a subject and positive duration.");
          setSaveBusy(false);
          return;
        }
        const res = await createTimeStudyEntry({
          session_id: sessionId,
          subject_label: d.subject_label,
          duration_seconds: d.duration_seconds,
          entry_kind: d.entry_kind ?? (d.recipe_id ? "recipe" : "freeform"),
          recipe_id: d.recipe_id ?? null,
          personnel_label: d.personnel_label ?? null,
          quantity: d.quantity ?? null,
          quantity_unit: d.quantity_unit ?? null,
          notes: d.notes ?? null,
          source: "dictation",
          transcript_excerpt: dictate.transcript.slice(0, 500),
        });
        if (!res.ok) {
          setSaveError("error" in res ? String(res.error) : "Save failed");
          setSaveBusy(false);
          return;
        }
      }
      onSaved();
      onClose();
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl dark:bg-zinc-900">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
            Dictate time study
          </h3>
          <button type="button" className={mfgSecondaryBtnClass} onClick={onClose}>
            Close
          </button>
        </div>

        {phase.kind === "record" ? (
          <div className="space-y-3">
            {!dictate.isSupported ? (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Speech recognition is not supported in this browser. Type entries instead.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-zinc-400">
                  Speak observations like “Eight by eight floor, forty-five minutes.” Stop when
                  finished — we extract and show a review list.
                </p>
                <div className="flex flex-wrap gap-2">
                  {dictate.state === "recording" ? (
                    <button
                      type="button"
                      className={mfgPrimaryBtnLgClass}
                      onClick={() => dictate.stop()}
                    >
                      Stop
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={mfgPrimaryBtnLgClass}
                      onClick={() => {
                        autoExtractRef.current = false;
                        dictate.reset();
                        dictate.start();
                      }}
                    >
                      Start recording
                    </button>
                  )}
                  {dictate.transcript.trim() && dictate.state !== "recording" ? (
                    <button
                      type="button"
                      className={mfgSecondaryBtnClass}
                      onClick={() => {
                        autoExtractRef.current = true;
                        void runExtract(dictate.transcript);
                      }}
                    >
                      Extract now
                    </button>
                  ) : null}
                </div>
                {dictate.error ? (
                  <p className="text-sm text-red-600" role="alert">
                    {dictate.error}
                  </p>
                ) : null}
                <pre className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-800 dark:bg-zinc-800 dark:text-zinc-100">
                  {dictate.transcript || "Transcript appears here…"}
                </pre>
              </>
            )}
          </div>
        ) : null}

        {phase.kind === "extracting" ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">Extracting observations…</p>
        ) : null}

        {phase.kind === "error" ? (
          <div className="space-y-3">
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {phase.message}
            </p>
            <button
              type="button"
              className={mfgSecondaryBtnClass}
              onClick={() => {
                autoExtractRef.current = false;
                setPhase({ kind: "record" });
                dictate.reset();
              }}
            >
              Try again
            </button>
          </div>
        ) : null}

        {phase.kind === "review" ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-zinc-400">
              Review and edit before saving to this session.
            </p>
            {drafts.map((d, idx) => (
              <div
                key={idx}
                className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700"
              >
                <label className="block text-xs text-gray-600 dark:text-zinc-400">
                  Subject
                  <input
                    className={`${inputClass} mt-1 w-full`}
                    value={d.subject_label}
                    onChange={(e) => updateDraft(idx, { subject_label: e.target.value })}
                  />
                </label>
                <label className="block text-xs text-gray-600 dark:text-zinc-400">
                  Duration (minutes)
                  <input
                    className={`${inputClass} mt-1 w-full max-w-[10rem]`}
                    type="number"
                    min={0.1}
                    step="any"
                    value={durationSecondsToMinutesInput(d.duration_seconds)}
                    onChange={(e) => {
                      const sec = minutesInputToSeconds(e.target.value);
                      if (sec != null) updateDraft(idx, { duration_seconds: sec });
                    }}
                  />
                </label>
                <label className="block text-xs text-gray-600 dark:text-zinc-400">
                  Personnel (optional)
                  <input
                    className={`${inputClass} mt-1 w-full`}
                    value={d.personnel_label ?? ""}
                    onChange={(e) =>
                      updateDraft(idx, { personnel_label: e.target.value || null })
                    }
                  />
                </label>
                <button
                  type="button"
                  className="text-xs text-red-600 hover:underline"
                  onClick={() => removeDraft(idx)}
                >
                  Remove
                </button>
              </div>
            ))}
            {saveError ? (
              <p className="text-sm text-red-600" role="alert">
                {saveError}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={mfgPrimaryBtnLgClass}
                disabled={saveBusy || drafts.length === 0}
                onClick={() => void commit()}
              >
                {saveBusy ? "Saving…" : `Save ${drafts.length} observation(s)`}
              </button>
              <button
                type="button"
                className={mfgSecondaryBtnClass}
                disabled={saveBusy}
                onClick={() => {
                  autoExtractRef.current = false;
                  setPhase({ kind: "record" });
                  dictate.reset();
                }}
              >
                Re-dictate
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
